import * as React from 'react';
import { css } from '@emotion/core';
import { Str } from '../../localization/translationKeys';
import LocalDiv from '../../localization/LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import CountDown from './CountDown';
import { COLORS } from '../../globalStyle/colors';
import { diffMs, TIME } from '../../language/time';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HIDE_ON_MOBILE } from '../../globalStyle/mediaQueries';
import Msg from '../../localization/Msg';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { useAppState } from '../../core/redux/hooks';


const styles = {
  root:          css`
    align-items: stretch;
    text-align: end;
  `,
  deadline:      css`
    justify-content: flex-end;
    margin-bottom: 9px;
  `,
  deadlineTitle: css`
    font-weight: bold;
  `,
  countdown:     css`
    flex-grow: 1;
    ${HIDE_ON_MOBILE};
  `,
  clockImg:      css`
    font-size: 18px;
    margin-top: 6px; /* font has funny padding, this compensates the image to align with that */
    margin-left: 14px;
    margin-right: 14px;
  `,
};


type Props = {
  deadline: string | Date | undefined;
  className?: string;
};

const getColor = (
  deadline: string | Date | undefined,
  now: Date,
): string => deadline && diffMs(deadline, now) > 0 && diffMs(deadline, now) < 2 * TIME.D ? COLORS.RED : COLORS.BLACK;


/**
 * DeadlineCounter: a component that renders a deadline message along with a countdown. Text turns read when the
 * deadline is less than 2 days out.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DeadlineCounter: React.FC<Props> = props => {
  const deadline                = React.useMemo(() => new Date(props.deadline ?? new Date()), [props.deadline]);
  const [txtColor, setTxtColor] = React.useState(getColor(props.deadline, new Date()));

  const onTick = React.useCallback(
    () => setTxtColor(getColor(props.deadline, new Date())),
    [props.deadline, setTxtColor],
  );

  const code = useAppState(state => state.locale.lang.code);

  // Can't render a deadline that doesn't exist
  if (!props.deadline) {
    return null;
  }

  return (
    <div css={[styles.root, FLEX_COLUMN, css`color: ${txtColor} !important;`]} className={props.className}>
      {/* Deadline */}
      <LocalDiv css={[FLEX_ROW, styles.deadline]}>
        <Msg str={Str.DeadlineColon} css={styles.deadlineTitle} />
        &nbsp;{deadline.toLocaleDateString(code)}
        &nbsp;{deadline.toLocaleTimeString(code)}
      </LocalDiv>

      {/* Time Remaining */}
      <LocalDiv css={[FLEX_ROW, styles.countdown]}>
        <FontAwesomeIcon icon={faClock} css={styles.clockImg} />
        <CountDown deadline={deadline} onTick={onTick} />
      </LocalDiv>
    </div>
  );
};

export default DeadlineCounter;
